import { useCallback, useMemo } from 'react';

import { Box } from '@mui/system';
import { DialogActions, Step, StepLabel, Stepper } from '@mui/material';
import { UserInfoFormValues } from './onboardingForm';
import { breadcrumb, log, logError } from '@utils/logging';
import { USER_INFO_STEP } from './OnboardingSteps';
import { SubmitHandler, useFormContext, useFormState } from 'react-hook-form';
import LoadingButton from '@atoms/LoadingButton';
import { toast } from 'react-toastify';
import { updateUserName } from '@modules/supabase/users';
import useAuth from '@hooks/useAuth';
import { StepIconComponent, StepperConnector } from '@components/molecules/Stepper';

const steps = ['User Info'];

interface Props {
    step: number;
    onClose: () => void;
    onPrevClick: () => void;
    onNextClick: () => void;
}

export default function NextBackButtonGroup({ step, onClose, onNextClick, onPrevClick }: Props) {
    const { authUser } = useAuth();
    const enableNext = useEnableNext(step);
    const { isSubmitting } = useFormState();
    const { handleSubmit } = useFormContext<UserInfoFormValues>();

    const onLastStep = step === USER_INFO_STEP;

    const onSubmit: SubmitHandler<UserInfoFormValues> = useCallback(
        (values) => {
            if (authUser?.id)
                toast
                    .promise(
                        updateUserName(
                            {
                                id: authUser?.id,
                                firstName: values.firstName,
                                lastName: values.lastName,
                            },
                            'onboarding submit',
                        ),
                        {
                            pending: 'Saving...',
                            success: 'Profile Saved!',
                            error: 'Error saving profile',
                        },
                    )
                    .catch((error: unknown) => {
                        breadcrumb('Error sending email onboarding', 'auth');
                        if (error instanceof Error) logError(error);
                    });
        },
        [authUser?.id],
    );

    // Some props for NextButton change depending on which step you're on
    const nextButtonProps: {
        onClick?: () => void;
        children: string;
    } = useMemo(() => {
        if (onLastStep) {
            return {
                children: 'Continue',
                onClick: () =>
                    handleSubmit(onSubmit, () =>
                        toast.error(`Invalid name, please provide your full name`),
                    )(),
                // type: 'submit',
            };
        } else {
            return {
                children: 'Next',
                onClick: onNextClick,
            };
        }
    }, [handleSubmit, onLastStep, onNextClick, onSubmit]);

    return (
        <Box>
            <Stepper
                alternativeLabel
                activeStep={step}
                connector={<StepperConnector />}
                sx={{ paddingBottom: 1 }}
            >
                {steps.length > 1
                    ? steps.map((label) => (
                          <Step key={label}>
                              <StepLabel StepIconComponent={StepIconComponent}>{label}</StepLabel>
                          </Step>
                      ))
                    : null}
            </Stepper>
            <DialogActions>
                <LoadingButton onClick={onClose}>Logout</LoadingButton>
                <Box sx={{ flex: '1 1 auto' }} />
                {step > 0 ? (
                    <LoadingButton type="button" onClick={onPrevClick}>
                        Prev
                    </LoadingButton>
                ) : null}

                <LoadingButton disabled={!enableNext} loading={isSubmitting} {...nextButtonProps} />
            </DialogActions>
            {/* {onLastStep && Object.keys(errors).length ? (
                    <>
                        {Object.keys(errors).map((errorKey) => (
                            <ErrorText
                                key={errorKey}
                                message={`${errorKey}: ${errors?.[errorKey]?.message}`}
                            />
                        ))}
                    </>
                ) : null} */}
        </Box>
    );
}

const userInfoFields: (keyof UserInfoFormValues)[] = ['firstName', 'lastName'];

function useEnableNext(step: number) {
    const { errors, dirtyFields } = useFormState<UserInfoFormValues>();

    const noErrorsForStep = useCallback(
        (fields: (keyof UserInfoFormValues)[]) =>
            !fields.some((field) => Object.keys(errors).includes(field)),
        [errors],
    );

    const everyFieldTouched = useCallback(
        (fields: (keyof UserInfoFormValues)[]) => fields.every((f) => dirtyFields[f]),
        [dirtyFields],
    );

    // console.log(`errors`, { errors, userInfoFields: noErrorsForStep(userInfoFields) });

    if (step === USER_INFO_STEP)
        return everyFieldTouched(userInfoFields) && noErrorsForStep(userInfoFields);
    else {
        log(`useEnableNext unrecognized step: ${step}`);
        return true;
    }
}
